
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchAvailableAgents, getAgentDetails } from "@/services/ai/elevenLabsService";
import { toast } from "@/components/ui/use-toast";
import { AgentFormDialog } from "./AgentFormDialog";
import { AgentDetailSheet } from "./AgentDetailSheet";
import { ElevenLabsAgent } from "@/types/admin";
import { AgentListHeader } from "./AgentListHeader";
import { AgentTable } from "./AgentTable";
import { EmptyAgentState } from "./EmptyAgentState";
import { LoadingState } from "./LoadingState";
import { ErrorState } from "./ErrorState";
import { createOrSyncAgent } from "@/services/admin/agentService";

export const AgentList = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<ElevenLabsAgent | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [agentDetails, setAgentDetails] = useState<any>(null);
  const [isAgentSyncing, setIsAgentSyncing] = useState(false);

  // Fetch ElevenLabs agents
  const {
    data: elevenLabsResponse,
    isLoading,
    error,
    refetch: refetchElevenLabsAgents
  } = useQuery({
    queryKey: ["elevenlabs-agents"],
    queryFn: fetchAvailableAgents,
    refetchOnWindowFocus: false
  });

  const elevenLabsAgents = elevenLabsResponse?.agents || [];

  // Create or sync agent mutation
  const syncAgentMutation = useMutation({
    mutationFn: createOrSyncAgent,
    onSuccess: (data) => {
      setIsAgentSyncing(false);
      setIsDetailOpen(true);
    },
    onError: (error) => {
      setIsAgentSyncing(false);
      toast({
        title: "Error",
        description: `Failed to sync agent: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    }
  });

  const handleRefresh = () => {
    refetchElevenLabsAgents();
    toast({
      title: "Refreshing agents",
      description: "Fetching the latest agents from ElevenLabs",
    });
  };

  const handleViewAgent = async (agent: ElevenLabsAgent) => {
    setSelectedAgent(agent);
    setIsAgentSyncing(true);
    
    try {
      // Fetch detailed information about the agent
      const response = await getAgentDetails(agent.agent_id);
      if (response.success && response.agent) {
        setAgentDetails(response.agent);
        
        // Extract phone number information from the agent details
        const phoneNumberId = 
          response.agent.agent_phone_number_id || 
          response.agent.phone_number_id ||
          (response.agent.conversation_config?.agent?.phone_number_id) || 
          null;
          
        // We don't have direct access to the phone number, only the ID
        
        // Sync the agent with our database
        syncAgentMutation.mutate({
          elevenlabs_agent_id: agent.agent_id,
          name: agent.name,
          description: agent.description || null,
          voice_id: response.agent.conversation_config?.tts?.voice_id || "",
          language: response.agent.conversation_config?.agent?.language || "en-US",
          dealership_id: null,
          first_message: response.agent.conversation_config?.agent?.first_message || null,
          max_duration_seconds: response.agent.conversation_config?.conversation?.max_duration_seconds || 120,
          goal: null,
          stability: response.agent.conversation_config?.tts?.stability || 0.7,
          speed: response.agent.conversation_config?.tts?.speed || 1.0,
          similarity_boost: response.agent.conversation_config?.tts?.similarity_boost || 0.85,
          optimize_latency: response.agent.conversation_config?.tts?.optimize_streaming_latency > 0 || true,
          phone_number_id: phoneNumberId,
          phone_number: null, // We don't have this information from the API
        });
      } else {
        setIsAgentSyncing(false);
        toast({
          title: "Error",
          description: response.error || "Could not fetch agent details",
          variant: "destructive",
        });
      }
    } catch (error) {
      setIsAgentSyncing(false);
      toast({
        title: "Error",
        description: "Failed to fetch agent details",
        variant: "destructive",
      });
    }
  };

  const onDetailClose = () => {
    setSelectedAgent(null);
    setAgentDetails(null);
    setIsDetailOpen(false);
  };

  // Force an initial refresh
  console.log("Agent list state:", { 
    isLoading, 
    error: error ? String(error) : null,
    agentsCount: elevenLabsAgents.length, 
    agents: elevenLabsAgents
  });

  return (
    <div className="space-y-6">
      <AgentListHeader 
        isLoading={isLoading || isAgentSyncing}
        onRefresh={handleRefresh}
        onCreateNew={() => setIsCreateDialogOpen(true)}
      />

      {error ? (
        <ErrorState error={error} />
      ) : isLoading ? (
        <LoadingState />
      ) : (
        <div className="space-y-8">
          {elevenLabsAgents && elevenLabsAgents.length > 0 ? (
            <AgentTable 
              agents={elevenLabsAgents} 
              onViewAgent={handleViewAgent} 
            />
          ) : (
            <EmptyAgentState onCreateNew={() => setIsCreateDialogOpen(true)} />
          )}
        </div>
      )}

      <AgentFormDialog 
        open={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={() => {
          refetchElevenLabsAgents();
          toast({
            title: "Agent created",
            description: "New agent has been created in ElevenLabs.",
          });
        }}
      />

      {selectedAgent && isDetailOpen && (
        <AgentDetailSheet
          elevenlabsAgentId={selectedAgent.agent_id}
          elevenLabsDetails={agentDetails}
          open={isDetailOpen}
          onOpenChange={setIsDetailOpen}
          onClose={onDetailClose}
        />
      )}

      {isAgentSyncing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
            <p>Syncing agent data...</p>
          </div>
        </div>
      )}
    </div>
  );
};
